import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import FlowWires, { wiresForPhase, type WireStory } from "./FlowWires";

/**
 * Doc-aligned use cases (02-business-processes, müşteri sunum, multi-source):
 * each quest plays the same canvas with a different animated path.
 */
type QuestId =
  | "prep-foundation"
  | "ad-hoc-kpi"
  | "save-dashboard"
  | "dashboard-runtime"
  | "secure-share"
  | "live-catalog"
  | "live-db";

type Phase =
  | "feed"
  | "derive"
  | "typing"
  | "shrink"
  | "launch"
  | "bind"
  | "answer"
  | "reveal"
  | "hold";

type Quest = {
  id: QuestId;
  badge: string;
  title: string;
  input: string;
  outputTitle: string;
  output: string;
  source: string;
  sourceLabel: string;
  feedLine: string;
  deriveLine: string;
  bindLine: string;
  answerLine: string;
  feedVia: "db" | "api" | "mcp";
  feedFocus: boolean;
  mcpLive: boolean;
  dbLive: boolean;
  path: "os" | "dashboard" | "share" | "mcp" | "db";
};

const QUESTS: Quest[] = [
  {
    id: "prep-foundation",
    badge: "01",
    title: "Unify data",
    input: "Merge last night’s CRM deals with order lines for reporting.",
    outputTitle: "Report layer ready",
    output:
      "Prep jobs plus manual Excel/CSV ingest landed in Connect AI DB. Guide RAG now knows the unified fields for chat and dashboards.",
    source: "crm",
    sourceLabel: "CRM",
    feedVia: "db",
    feedFocus: true,
    mcpLive: false,
    dbLive: false,
    path: "os",
    feedLine:
      "In-hub DB Adaptors pull systems into Prep/Ingest jobs; operators can also upload Excel/CSV — then Index writes Connect AI DB.",
    deriveLine:
      "Mapping and prep-guide docs are approved into Guide RAG so the platform can explain the joined model.",
    bindLine:
      "Chat API delivers the question; the resource router binds the new unified Connect AI DB indices.",
    answerLine:
      "Chat confirms the report layer is ready — widgets and ad-hoc questions can now read the indexed join.",
  },
  {
    id: "ad-hoc-kpi",
    badge: "02",
    title: "Ask KPIs",
    input: "Channel revenue and refund rate for this month?",
    outputTitle: "KPI answer",
    output:
      "Chart + table from Connect AI DB aggregations, grounded by Guide RAG — answered without calling ERP live.",
    source: "erp",
    sourceLabel: "ERP",
    feedVia: "db",
    feedFocus: false,
    mcpLive: false,
    dbLive: false,
    path: "os",
    feedLine:
      "Jobs keep feeding finance indices in the background so chat always has fresh facts to read.",
    deriveLine: "Guide RAG holds margin and refund definitions from approved mapping docs.",
    bindLine:
      "After Chat API, the router prefers the finance Connect AI DB index — not a direct hop to Source Worlds.",
    answerLine: "RAG_Guide + Connect AI DB compose channel revenue and refund rate for the month.",
  },
  {
    id: "save-dashboard",
    badge: "03",
    title: "Save widget",
    input: "Save channel revenue as a widget for the exec board.",
    outputTitle: "Widget pinned",
    output:
      "The answer becomes a saved widget on the exec dashboard — same Connect AI DB DSL, no re-prompt required.",
    source: "erp",
    sourceLabel: "ERP",
    feedVia: "db",
    feedFocus: false,
    mcpLive: false,
    dbLive: false,
    path: "dashboard",
    feedLine: "Indexed finance data already sits in Connect AI DB from earlier Prep/Ingest jobs.",
    deriveLine: "Guide RAG keeps field names consistent when Chat drafts the widget DSL.",
    bindLine: "Router binds the revenue index; Chat plans a widget draft for Dashboards.",
    answerLine: "Dashboards stores the widget — leaders can refresh it later without another chat turn.",
  },
  {
    id: "dashboard-runtime",
    badge: "04",
    title: "Run board",
    input: "Refresh the exec dashboard for the last 30 days.",
    outputTitle: "Exec board live",
    output:
      "Dashboard execute runs parallel Connect AI DB reads for the shared date range — no LLM on refresh.",
    source: "ecom",
    sourceLabel: "Commerce",
    feedVia: "api",
    feedFocus: false,
    mcpLive: false,
    dbLive: false,
    path: "dashboard",
    feedLine: "Always-on jobs keep commerce and finance indices current behind the scenes.",
    deriveLine: "Approved guides stay available, but this refresh is pure dashboard execute.",
    bindLine: "Chat API (or board UI) asks for a 30-day refresh; router points widgets at Connect AI DB.",
    answerLine: "Dashboards tile lights as widgets read Connect AI DB in parallel and render the board.",
  },
  {
    id: "secure-share",
    badge: "05",
    title: "Secure share",
    input: "Share a password-protected snapshot with the board through Friday.",
    outputTitle: "Share link ready",
    output:
      "Share & mail publishes a snapshot link — recipients see trusted results without new accounts.",
    source: "erp",
    sourceLabel: "ERP",
    feedVia: "db",
    feedFocus: false,
    mcpLive: false,
    dbLive: false,
    path: "share",
    feedLine: "Snapshot data still comes from job-fed Connect AI DB indices, not a live ERP scrape.",
    deriveLine: "Guides document what the shared pack contains for audit clarity.",
    bindLine: "Owner request enters via Chat API; router reuses dashboard/Connect AI DB resources.",
    answerLine: "Share & mail freezes the range and issues a password-protected link through Friday.",
  },
  {
    id: "live-catalog",
    badge: "06",
    title: "Live MCP",
    input: "Summarize this quote’s status and line items before the call.",
    outputTitle: "Live quote brief",
    output:
      "Connect AI DB was insufficient — OpenAPI MCP used registered catalogs to call external CRM APIs; Chat composed the brief via Chat API.",
    source: "crm",
    sourceLabel: "CRM",
    feedVia: "mcp",
    feedFocus: true,
    mcpLive: true,
    dbLive: false,
    path: "mcp",
    feedLine:
      "OpenAPI catalogs can also feed Prep, but this story is the live path: catalogs describe the external APIs MCP will call.",
    deriveLine:
      "Catalog schemas land in Guide RAG; in-hub OpenAPI MCP is ready to invoke those registered external APIs.",
    bindLine:
      "Router tries Connect AI DB first, then OpenAPI MCP → OpenAPI catalogs → external HTTP APIs on the CRM side.",
    answerLine:
      "External API results return through MCP into Chat, then Chat API delivers the outcome — mutations stay approve-only.",
  },
  {
    id: "live-db",
    badge: "07",
    title: "Live DB",
    input: "What is ACME’s open order balance in ERP right now?",
    outputTitle: "Live ERP balance",
    output:
      "Connect AI DB was not fresh enough — the resource router used in-hub DB Adaptors to query the ERP database and Chat returned the live balance.",
    source: "erp",
    sourceLabel: "ERP",
    feedVia: "db",
    feedFocus: false,
    mcpLive: false,
    dbLive: true,
    path: "db",
    feedLine:
      "DB Adaptors also feed Prep jobs from source databases; indices stay warm in the background.",
    deriveLine:
      "Guide RAG knows which ERP tables and keys the adaptors can query when chat needs live facts.",
    bindLine:
      "Router tries Connect AI DB first, then opens in-hub DB Adaptors toward the external ERP database.",
    answerLine:
      "DB Adaptors pull live rows from the source DB; results return into Chat for the answer.",
  },
];

const HUB_MODULES: {
  id: string;
  label: string;
  icon: string;
  col: number;
  row: number;
  sub?: string;
  tone?: "hub" | "write" | "db" | "mcp" | "api";
}[] = [
  // Left column — jobs top → bottom, Service API at bottom-left
  { id: "prep", label: "Prep jobs", icon: "⚙", col: 0, row: 0, tone: "write", sub: "Graphs · joins" },
  { id: "ingest", label: "Ingest jobs", icon: "⇩", col: 0, row: 1, tone: "write", sub: "Excel · CSV · API" },
  { id: "index", label: "Index write", icon: "◫", col: 0, row: 2, tone: "write", sub: "→ Connect AI DB" },
  { id: "service-api", label: "Service API", icon: "⇄", col: 0, row: 3, tone: "api", sub: "Upload · ingest" },
  // Core modules
  { id: "router", label: "Resource router", icon: "◎", col: 1, row: 0 },
  { id: "chat", label: "Chat & AI", icon: "✦", col: 2, row: 0 },
  { id: "guide", label: "Guide RAG", icon: "◇", col: 1, row: 1 },
  { id: "opensearch", label: "Connect AI DB", icon: "◫", col: 2, row: 1 },
  { id: "db", label: "DB Adaptors", icon: "⬢", col: 1, row: 2, tone: "db", sub: "In-hub · live SQL" },
  { id: "mcp", label: "OpenAPI MCP", icon: "⬡", col: 2, row: 2, tone: "mcp", sub: "Catalogs → ext APIs" },
  { id: "dashboards", label: "Dashboards", icon: "▣", col: 1, row: 3 },
  { id: "share", label: "Share & mail", icon: "➚", col: 2, row: 3 },
];

/** Manual uploads from outside the live systems */
const FILE_UPLOADS = [
  { id: "xlsx", label: "Excel", sub: ".xlsx · .xls", icon: "▤" },
  { id: "csv", label: "CSV", sub: ".csv · upload", icon: "☰" },
];

const SOURCES = [
  { id: "erp", label: "ERP", sub: "Finance" },
  { id: "ecom", label: "Commerce", sub: "Orders" },
  { id: "crm", label: "CRM", sub: "Pipeline" },
  { id: "internal", label: "Internal", sub: "Ops" },
];

const PHASE_MS: Record<Phase, number> = {
  feed: 4800,
  derive: 4200,
  typing: 0,
  shrink: 1100,
  launch: 1400,
  bind: 4000,
  answer: 4500,
  reveal: 0,
  hold: 5000,
};

function IsoBlock({
  title,
  sub,
  active,
  tone = "default",
  size = "md",
  icon,
  dimmed,
  working,
  wirePort,
}: {
  title: string;
  sub?: string;
  active?: boolean;
  tone?: "default" | "db" | "api" | "mcp" | "input" | "output" | "hub" | "write";
  // mcp tone = OpenAPI MCP inside hub / catalog gate
  size?: "sm" | "md" | "lg";
  icon?: string;
  dimmed?: boolean;
  /** Show spinning work badge on the isometric cube’s top-left corner */
  working?: boolean;
  /** Expose iso top-left as wire attachment (hub modules) */
  wirePort?: boolean;
}) {
  return (
    <div
      className={[
        "gar-iso",
        `gar-iso--${size}`,
        `gar-iso--${tone}`,
        active ? "gar-iso--active" : "",
        dimmed ? "gar-iso--dim" : "",
        working ? "gar-iso--working" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="gar-iso__cube">
        <div className="gar-iso__face gar-iso__face--top" />
        <div className="gar-iso__face gar-iso__face--left" />
        <div className="gar-iso__face gar-iso__face--right">
          <div className="gar-iso__content">
            {icon ? <span className="gar-iso__icon">{icon}</span> : null}
            <strong>{title}</strong>
            {sub ? <span>{sub}</span> : null}
          </div>
        </div>
        {wirePort || working ? (
          <span className="gar-iso__port" data-wire-port aria-hidden />
        ) : null}
        {working ? (
          <span className="gar-iso__busy" aria-hidden>
            <i className="gar-iso__busy-ring" />
            <i className="gar-iso__busy-ring gar-iso__busy-ring--delay" />
            <span className="gar-iso__busy-glyph">{icon || "●"}</span>
          </span>
        ) : null}
      </div>
      {active ? <span className="gar-iso__glow" aria-hidden /> : null}
    </div>
  );
}

function useTypewriter(text: string, active: boolean, paused: boolean, cps = 26) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setCount(0);
      return undefined;
    }
    setCount(0);
    return undefined;
  }, [text, active]);

  useEffect(() => {
    if (!active || paused) return undefined;
    if (count >= text.length) return undefined;
    const delay = text[count] === " " ? 40 : 1000 / cps + Math.random() * 35;
    const t = window.setTimeout(() => setCount((c) => c + 1), delay);
    return () => window.clearTimeout(t);
  }, [active, paused, count, text, cps]);

  return {
    visible: text.slice(0, count),
    done: active && count >= text.length,
    count,
  };
}

function captionFor(phase: Phase, quest: Quest): { chapter: string; line: string } {
  switch (phase) {
    case "feed":
      return { chapter: "Act I — Feed the platform", line: quest.feedLine };
    case "derive":
      return { chapter: "Act II — Derive knowledge", line: quest.deriveLine };
    case "typing":
      return {
        chapter: "Act III — Ask",
        line: "Player Ask expands — the question types itself into the message box.",
      };
    case "shrink":
      return {
        chapter: "Act III — Fold",
        line: "The composer shrinks back into the Player Ask tile.",
      };
    case "launch":
      return {
        chapter: "Act III — Send",
        line: "The message leaves Player Ask, crosses the Chat API layer, then lands in Connect AI.",
      };
    case "bind":
      return { chapter: "Act IV — Bind & read", line: quest.bindLine };
    case "answer":
      return { chapter: "Act V — Compose", line: quest.answerLine };
    case "reveal":
    case "hold":
      return { chapter: "Act VI — Outcome", line: quest.output };
    default:
      return { chapter: "", line: "" };
  }
}

export default function ArchitectureDiagram() {
  const [questId, setQuestId] = useState<QuestId>("prep-foundation");
  const [phase, setPhase] = useState<Phase>("feed");
  const [paused, setPaused] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const worldRef = useRef<HTMLDivElement>(null);

  const quest = useMemo(() => QUESTS.find((q) => q.id === questId) || QUESTS[0], [questId]);
  const typing = useTypewriter(quest.input, phase === "typing", paused, 26);
  const caption = captionFor(phase, quest);
  const wireStory = useMemo<WireStory>(
    () => ({
      sourceId: quest.source,
      feedVia: quest.feedVia,
      feedFocus: quest.feedFocus && phase === "feed",
      mcpLive: quest.mcpLive,
      dbLive: quest.dbLive,
      path: quest.path,
    }),
    [quest, phase]
  );
  const wireLinks = useMemo(
    () => (paused ? [] : wiresForPhase(phase, wireStory)),
    [phase, wireStory, paused]
  );

  const restartQuest = useCallback((id: QuestId) => {
    setQuestId(id);
    setPhase("feed");
    setRunKey((k) => k + 1);
  }, []);

  const goNextQuest = useCallback(() => {
    const qi = QUESTS.findIndex((q) => q.id === questId);
    restartQuest(QUESTS[(qi + 1) % QUESTS.length].id);
  }, [questId, restartQuest]);

  useEffect(() => {
    if (phase !== "typing" || !typing.done || paused) return undefined;
    const t = window.setTimeout(() => setPhase("shrink"), 500);
    return () => window.clearTimeout(t);
  }, [phase, typing.done, paused]);

  useEffect(() => {
    if (paused) return undefined;
    if (phase === "typing") return undefined;

    if (phase === "reveal") {
      const t = window.setTimeout(() => setPhase("hold"), 900);
      return () => window.clearTimeout(t);
    }

    if (phase === "hold") {
      const t = window.setTimeout(goNextQuest, PHASE_MS.hold);
      return () => window.clearTimeout(t);
    }

    const order: Phase[] = [
      "feed",
      "derive",
      "typing",
      "shrink",
      "launch",
      "bind",
      "answer",
      "reveal",
    ];
    const idx = order.indexOf(phase);
    if (idx < 0 || idx >= order.length - 1) return undefined;
    if (order[idx + 1] === "typing") {
      const t = window.setTimeout(() => setPhase("typing"), PHASE_MS[phase]);
      return () => window.clearTimeout(t);
    }
    if (
      phase === "shrink" ||
      phase === "launch" ||
      phase === "bind" ||
      phase === "answer" ||
      phase === "feed" ||
      phase === "derive"
    ) {
      const t = window.setTimeout(() => setPhase(order[idx + 1]), PHASE_MS[phase]);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [phase, paused, goNextQuest, runKey]);

  const lit = useMemo(() => {
    const s = new Set<string>();
    if (phase === "feed") {
      s.add(quest.source);
      s.add(quest.feedVia);
      s.add("file-manual");
      s.add("service-api");
      s.add("prep");
      s.add("ingest");
      s.add("index");
      s.add("opensearch");
      if (quest.feedVia === "mcp") {
        s.add("mcp");
        s.add("mcp-gate");
        s.add("ext-api");
      }
      if (quest.feedVia === "db") s.add("db");
      s.add("hub");
      return s;
    }
    if (phase === "derive") {
      s.add("index");
      s.add("prep");
      s.add("ingest");
      s.add("guide");
      s.add("opensearch");
      s.add("router");
      if (quest.mcpLive || quest.feedVia === "mcp") {
        s.add("mcp");
        s.add("mcp-gate");
        s.add("ext-api");
      }
      if (quest.feedVia === "db" || quest.dbLive) s.add("db");
      s.add("hub");
      return s;
    }
    if (phase === "typing" || phase === "shrink" || phase === "launch") {
      s.add("input");
      if (phase === "launch") {
        s.add("chat-api");
        s.add("hub");
      }
      return s;
    }
    if (phase === "bind") {
      s.add("chat-api");
      s.add("chat");
      s.add("router");
      s.add("hub");
      if (quest.mcpLive) {
        s.add("mcp");
        s.add("mcp-gate");
        s.add("ext-api");
        s.add(quest.source);
        s.add("opensearch");
      } else if (quest.dbLive || quest.path === "db") {
        s.add("db");
        s.add("db-gate");
        s.add(quest.source);
        s.add("opensearch");
        s.add("guide");
      } else {
        s.add("opensearch");
        s.add("guide");
      }
      return s;
    }
    if (phase === "answer") {
      s.add("chat");
      s.add("hub");
      s.add("chat-api");
      if (quest.path === "mcp" || quest.mcpLive) {
        s.add("mcp");
        s.add("mcp-gate");
        s.add("ext-api");
        s.add(quest.source);
        s.add("guide");
        s.add("router");
      } else if (quest.path === "db" || quest.dbLive) {
        s.add("db");
        s.add("db-gate");
        s.add(quest.source);
        s.add("router");
        s.add("guide");
      } else if (quest.path === "dashboard") {
        s.add("dashboards");
        s.add("opensearch");
      } else if (quest.path === "share") {
        s.add("share");
        s.add("dashboards");
        s.add("opensearch");
      } else {
        s.add("guide");
        s.add("opensearch");
        s.add("dashboards");
      }
      return s;
    }
    if (phase === "reveal" || phase === "hold") {
      s.add("output");
      s.add("chat-api");
      if (quest.path === "share") s.add("share");
      else if (quest.path === "dashboard") s.add("dashboards");
      else s.add("chat");
      return s;
    }
    return s;
  }, [phase, quest]);

  const isOn = (id: string) => lit.has(id);

  const stageMode =
    phase === "typing" || phase === "shrink"
      ? "ask"
      : phase === "launch"
        ? "launch"
        : phase === "reveal" || phase === "hold"
          ? "reveal"
          : phase === "feed" || phase === "derive"
            ? "prep"
            : "flow";

  const progressSteps: Phase[] = [
    "feed",
    "derive",
    "typing",
    "shrink",
    "launch",
    "bind",
    "answer",
    "reveal",
  ];
  const progressIndex =
    phase === "hold" ? progressSteps.length - 1 : Math.max(0, progressSteps.indexOf(phase));

  const askExpanded = phase === "typing" || phase === "shrink";
  const outcomeExpanded = phase === "reveal" || phase === "hold";
  const [hubFocusBox, setHubFocusBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!askExpanded && !outcomeExpanded) {
      setHubFocusBox(null);
      return undefined;
    }
    const world = worldRef.current;
    const hub = world?.querySelector(".gar-hub-arena");
    if (!world || !hub) return undefined;

    const place = () => {
      const wr = world.getBoundingClientRect();
      const hr = hub.getBoundingClientRect();
      const width = hr.width * 0.6;
      const height = hr.height * 0.6;
      setHubFocusBox({
        left: hr.left - wr.left + (hr.width - width) / 2,
        top: hr.top - wr.top + (hr.height - height) / 2,
        width,
        height,
      });
    };

    place();
    const ro = new ResizeObserver(place);
    ro.observe(world);
    ro.observe(hub);
    window.addEventListener("resize", place);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [askExpanded, outcomeExpanded, runKey, phase]);

  return (
    <div className={`gar-game gar-game--${stageMode}`} key={runKey}>
      <div className="gar-game__hud">
        <div className="gar-game__quests" role="tablist" aria-label="Story quests">
          {QUESTS.map((q) => (
            <button
              key={q.id}
              type="button"
              role="tab"
              aria-selected={q.id === questId}
              className={`gar-game__quest ${q.id === questId ? "gar-game__quest--on" : ""}`}
              onClick={() => restartQuest(q.id)}
            >
              <span className="gar-game__quest-badge">{q.badge}</span>
              {q.title}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="gar-game__pause"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
        >
          {paused ? "Play story" : "Pause"}
        </button>
      </div>

      <div className="gar-game__caption" key={`${quest.id}-${phase}`}>
        <span className="gar-game__chapter">{caption.chapter}</span>
        <p>{caption.line}</p>
        <div className="gar-game__progress" aria-hidden>
          {progressSteps.map((step, i) => (
            <span
              key={step}
              className={`gar-game__pip ${i === progressIndex ? "gar-game__pip--on" : ""} ${
                i < progressIndex ? "gar-game__pip--done" : ""
              }`}
            />
          ))}
        </div>
      </div>

      <div className={`gar-game__stage gar-game__stage--${stageMode}`}>
        <div className="gar-game__grid" aria-hidden />
        <div className="gar-game__aurora" aria-hidden />

        <div
          ref={worldRef}
          className={`gar-world gar-world--arch ${
            askExpanded || outcomeExpanded ? "gar-world--focus-hub" : ""
          }`}
        >
          <FlowWires
            containerRef={worldRef}
            links={wireLinks}
            active={!paused && wireLinks.length > 0}
            revision={`${runKey}-${phase}-${quest.id}`}
          />

          {(askExpanded || outcomeExpanded) && hubFocusBox ? (
            <div
              className={`gar-hub-focus ${
                askExpanded ? "gar-hub-focus--ask" : "gar-hub-focus--out"
              } ${
                phase === "shrink"
                  ? "gar-hub-focus--shrink"
                  : phase === "hold"
                    ? "gar-hub-focus--held"
                    : "gar-hub-focus--grow"
              }`}
              style={hubFocusBox}
              aria-live="polite"
            >
              {askExpanded ? (
                <>
                  <div className="gar-pop__chrome">
                    <span className="gar-pop__dot" />
                    <span className="gar-pop__dot" />
                    <span className="gar-pop__dot" />
                    <span className="gar-pop__title">Ask Connect AI</span>
                  </div>
                  <div className="gar-pop__field gar-hub-focus__field">
                    <span className="gar-pop__text">
                      {phase === "typing" ? typing.visible : quest.input}
                      {phase === "typing" && !typing.done ? (
                        <span className="gar-pop__caret" aria-hidden />
                      ) : null}
                    </span>
                  </div>
                  <div className="gar-pop__keys" aria-hidden>
                    {["Q", "W", "E", "R", "T", "Y"].map((k, i) => (
                      <span
                        key={k}
                        className={`gar-pop__key ${
                          phase === "typing" && !typing.done && typing.count % 6 === i
                            ? "gar-pop__key--press"
                            : ""
                        }`}
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                  <div className="gar-pop__send">
                    {phase === "shrink" ? "Returning…" : typing.done ? "Ready" : "Typing…"}
                  </div>
                </>
              ) : (
                <>
                  <div className="gar-pop__badge">Outcome unlocked</div>
                  <div className="gar-pop__chart" aria-hidden>
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                  <h4>{quest.outputTitle}</h4>
                  <p>{quest.output}</p>
                  <div className="gar-pop__query">Q: {quest.input}</div>
                </>
              )}
            </div>
          ) : null}

          {/* Sources + gates — always-on feed origin on the canvas */}
          <section
            className={`gar-zone gar-zone--sources ${
              phase === "feed" ? "gar-zone--lit" : "gar-zone--feed-alive"
            }`}
          >
            <h3 className="gar-zone__label">Source Worlds · feed</h3>
            <div className="gar-zone__stack">
              {SOURCES.map((s) => (
                <div key={s.id} data-wire={`src-${s.id}`} className="gar-wire-anchor">
                  <IsoBlock
                    title={s.label}
                    sub={s.sub}
                    size="sm"
                    icon="▣"
                    active={isOn(s.id) || s.id === quest.source}
                    dimmed={s.id !== quest.source && phase === "feed"}
                  />
                </div>
              ))}
            </div>
            <div className="gar-gates">
              <div data-wire="gate-db" className="gar-wire-anchor">
                <IsoBlock
                  title="Source DB"
                  sub="External"
                  tone="db"
                  size="sm"
                  active={
                    quest.feedVia === "db" || isOn("db-gate") || quest.dbLive || quest.path === "db"
                  }
                  dimmed={
                    quest.feedVia !== "db" && !quest.dbLive && quest.path !== "db" && !isOn("db-gate")
                  }
                />
              </div>
              <div data-wire="gate-api" className="gar-wire-anchor">
                <IsoBlock
                  title="API"
                  sub="Ingest"
                  tone="api"
                  size="sm"
                  active={quest.feedVia === "api"}
                  dimmed={quest.feedVia !== "api"}
                />
              </div>
              <div className="gar-gates__mcp">
                <div data-wire="gate-mcp" className="gar-wire-anchor">
                  <IsoBlock
                    title="OpenAPI Catalogs"
                    sub="Registered specs"
                    tone="mcp"
                    size="sm"
                    active={
                      quest.feedVia === "mcp" || isOn("mcp-gate") || quest.mcpLive || quest.path === "mcp"
                    }
                    dimmed={
                      quest.feedVia !== "mcp" &&
                      !quest.mcpLive &&
                      quest.path !== "mcp" &&
                      !isOn("mcp-gate")
                    }
                  />
                </div>
                <div data-wire="ext-api" className="gar-wire-anchor">
                  <IsoBlock
                    title="External APIs"
                    sub="HTTP · live tools"
                    tone="api"
                    size="sm"
                    active={isOn("ext-api") || quest.mcpLive || quest.path === "mcp"}
                    dimmed={!quest.mcpLive && quest.path !== "mcp" && !isOn("ext-api")}
                  />
                </div>
              </div>
            </div>

            <div
              data-wire="file-manual"
              className={`gar-files gar-wire-anchor ${
                isOn("file-manual") || phase === "feed" ? "gar-files--on" : ""
              }`}
            >
              <h3 className="gar-zone__label">Manual files</h3>
              <div className="gar-files__row">
                {FILE_UPLOADS.map((f) => (
                  <div key={f.id}>
                    <IsoBlock
                      title={f.label}
                      sub={f.sub}
                      size="sm"
                      icon={f.icon}
                      tone="write"
                      active={isOn("file-manual") || phase === "feed"}
                    />
                  </div>
                ))}
              </div>
              <p className="gar-files__hint">→ Service API inside hub</p>
            </div>
          </section>

          {/* Hub — includes Prep / Ingest / Index jobs */}
          <section
            className={`gar-zone gar-zone--hub ${
              isOn("hub") || phase === "launch" || phase === "feed" || phase === "derive"
                ? "gar-zone--lit"
                : ""
            }`}
          >
            <h3 className="gar-zone__label">Connect AI</h3>
            <div
              data-wire="hub"
              className={`gar-hub-arena ${
                phase === "launch" || phase === "bind" || phase === "derive" || phase === "feed"
                  ? "gar-hub-arena--catch"
                  : ""
              }`}
            >
              {(phase === "launch" || phase === "bind") && (
                <div className="gar-hub-arena__catch-msg" aria-hidden>
                  {quest.input}
                </div>
              )}
              {phase === "feed" && (
                <div className="gar-hub-arena__catch-msg gar-hub-arena__catch-msg--write" aria-hidden>
                  Prep → Ingest → Index · {quest.sourceLabel}
                </div>
              )}
              {phase === "derive" && (
                <div className="gar-hub-arena__catch-msg gar-hub-arena__catch-msg--derive" aria-hidden>
                  Building guides &amp; embeddings…
                </div>
              )}
              <div className="gar-hub-arena__modules">
                {HUB_MODULES.map((m) => {
                  const litTile =
                    isOn(m.id) ||
                    ((m.id === "prep" ||
                      m.id === "ingest" ||
                      m.id === "index" ||
                      m.id === "service-api") &&
                      (phase === "feed" || phase === "derive"));
                  const tone = m.tone ?? "hub";
                  const wireId = m.id === "service-api" ? "service-api" : `hub-${m.id}`;
                  return (
                    <div
                      key={m.id}
                      data-wire={wireId}
                      className={`gar-hub-tile gar-wire-anchor ${litTile ? "gar-hub-tile--on" : ""} ${
                        m.id === "mcp" ? "gar-hub-tile--mcp" : ""
                      } ${m.id === "db" ? "gar-hub-tile--db" : ""} ${
                        tone === "write" ? "gar-hub-tile--job" : ""
                      } ${m.id === "service-api" ? "gar-hub-tile--service" : ""}`}
                      style={{ gridColumn: m.col + 1, gridRow: m.row + 1 }}
                    >
                      <IsoBlock
                        title={m.label}
                        icon={m.icon}
                        tone={tone}
                        size="sm"
                        active={litTile}
                        working={litTile}
                        wirePort
                        sub={m.sub}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="gar-feed-badge gar-feed-badge--hub" aria-hidden>
                <span className="gar-feed-badge__dot" />
                Jobs live inside Connect AI
              </div>
            </div>
          </section>

          {/* Ask → Chat API → Outcome */}
          <section className="gar-zone gar-zone--right">
            <div
              className={`gar-anchor gar-anchor--ask ${
                askExpanded ? "gar-anchor--open" : ""
              } ${phase === "shrink" ? "gar-anchor--closing" : ""} ${
                phase === "typing" || phase === "shrink" || phase === "launch" ? "gar-zone--lit" : ""
              }`}
            >
              <h3 className="gar-zone__label">Player ask</h3>
              <div data-wire="ask" className="gar-anchor__tile gar-wire-anchor">
                <IsoBlock
                  title="Message"
                  sub="User question"
                  tone="input"
                  size="md"
                  active={isOn("input") || askExpanded}
                  icon="⌘"
                  dimmed={phase === "feed" || phase === "derive"}
                />
                {phase === "launch" || askExpanded ? (
                  <span className="gar-anchor__pulse" aria-hidden />
                ) : null}
              </div>
            </div>

            <div
              data-wire="chat-api"
              className={`gar-chat-api gar-wire-anchor ${
                isOn("chat-api") ||
                phase === "launch" ||
                phase === "bind" ||
                phase === "answer" ||
                phase === "reveal" ||
                phase === "hold"
                  ? "gar-chat-api--on"
                  : ""
              }`}
            >
              <span className="gar-chat-api__tag">API</span>
              <IsoBlock
                title="Chat API"
                sub="Ask in · Answer out"
                tone="api"
                size="sm"
                icon="⇄"
                active={
                  isOn("chat-api") ||
                  phase === "launch" ||
                  phase === "bind" ||
                  phase === "answer" ||
                  phase === "reveal" ||
                  phase === "hold"
                }
              />
              <p className="gar-chat-api__hint">Always via API — never hub → outcome direct</p>
            </div>

            <div
              className={`gar-anchor gar-anchor--out ${
                outcomeExpanded ? "gar-anchor--open" : ""
              } ${isOn("output") ? "gar-zone--lit" : ""}`}
            >
              <h3 className="gar-zone__label">Outcome</h3>
              <div data-wire="outcome" className="gar-anchor__tile gar-wire-anchor">
                <IsoBlock
                  title="Answer"
                  sub="Read path"
                  tone="output"
                  size="md"
                  active={isOn("output") || outcomeExpanded}
                  icon="★"
                  dimmed={phase === "feed" || phase === "derive"}
                />
                {outcomeExpanded ? <span className="gar-anchor__pulse" aria-hidden /> : null}
              </div>
            </div>

            {(phase === "bind" || phase === "answer" || phase === "reveal") && (
              <p className="gar-zone__hint gar-zone__hint--read">
                {phase === "answer" || phase === "reveal"
                  ? "Return: hub → Chat API → Outcome"
                  : quest.dbLive || quest.path === "db"
                    ? "Read: Connect AI DB first → DB Adaptors → source DB"
                    : quest.mcpLive || quest.path === "mcp"
                      ? "Read: Connect AI DB first → OpenAPI MCP → Catalogs → External APIs"
                      : "Read: Connect AI DB first"}
                {phase === "bind" ? ` · origin ${quest.sourceLabel}` : ""}
              </p>
            )}
          </section>

        </div>
      </div>

    </div>
  );
}
